/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {withTranslation} from 'react-i18next';
import {translate} from '@ion/core/src/locale/locale';
import {IonSnackbar} from '@ion/dls/src/notifications/IonSnackbar';
import {useNetworkStatus} from '../hooks/useNetworkStatus';

/**
 * @private
 *
 * Provides the default component implementation prior to being wrapped in higher-order
 * components and exported.
 *
 * @see {@link OfflineNotification}
 */
const Notification = () => {
  const isOnline: boolean = useNetworkStatus();

  return (isOnline === false) ? (
    <IonSnackbar message={translate('network:offline.notification')}
      type='error'
      show />
  ) : null;
};

/**
 * The `OfflineNotification` Snackbar provides the standard User Notification to be used
 * by application pages. When the application goes offline, the component will be displayed
 * and continue to display until the user's network connection has been restored.
 *
 * ```typescript
 *   import {OfflineNotification} from '@ion/applications/src/prompts/OfflineNotification';
 *
 *   <>
 *     <PageTitle namespace={'admin-assets'} initialized={tReady} />
 *     {tReady ? <AdminAssets /> : <ContentLoader fullPage />}
 *     <OfflineNotification />
 *   </>
 * ```
 *
 * The following translation entries are required and must be defined in the applications
 * `public/network.json` translations document:
 *
 * ```json
 *   "network": {
 *     "offline": {
 *       "notification": "...",
 *     },
 *     ...
 *   }
 * ```
 *
 * @see {@link useNetworkStatus}
 * @see {@link NetworkStatusPrompt}
 */
export const OfflineNotification = React.memo(
  withTranslation(['network'])(
    Notification
  ));

export default OfflineNotification;
